import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Share, X, CheckCircle2, Smartphone } from 'lucide-react';

// Capture l'événement beforeinstallprompt au niveau module (il peut arriver avant le mount)
let deferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event('cv-can-install'));
  });
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const InstallPwaButton = ({ onDone }) => {
  const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const on = () => setCanPrompt(true);
    window.addEventListener('cv-can-install', on);
    return () => window.removeEventListener('cv-can-install', on);
  }, []);

  if (isStandalone()) return null;

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') { setInstalled(true); if (onDone) onDone(); }
      deferredPrompt = null;
      setCanPrompt(false);
      return;
    }
    setShowIosHelp(true);
  };

  if (installed) {
    return (
      <div className="w-full flex items-center gap-3 p-3 rounded-xl bg-emerald-950/25 border border-emerald-700/30 text-emerald-300" data-testid="pwa-installed">
        <CheckCircle2 size={15} />
        <span className="text-[13.5px] font-medium">Application installée ✓</span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        data-testid="pwa-install-btn"
        onClick={install}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-red-600/15 border border-red-600/40 text-white hover:bg-red-600/25"
      >
        <Smartphone size={15} className="text-red-400" />
        <span className="flex-1 text-left">
          <span className="text-[13.5px] font-semibold block">Installer l'application</span>
          <span className="text-white/50 text-[11px]">Icône sur l'écran d'accueil · plein écran</span>
        </span>
        <Download size={14} className="text-white/50" />
      </button>

      {showIosHelp && createPortal(
        <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-end justify-center" onClick={() => setShowIosHelp(false)} data-testid="pwa-ios-help">
          <div className="bg-[#0d0d0e] border-t border-white/15 rounded-t-3xl p-6 pb-10 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif-editorial text-white text-[20px]">Installer sur {isIos() ? 'iPhone / iPad' : 'ce téléphone'}</h3>
              <button onClick={() => setShowIosHelp(false)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70"><X size={15} /></button>
            </div>
            {isIos() ? (
              <ol className="space-y-3 text-white/80 text-[13.5px]">
                <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-red-600/20 border border-red-700/40 text-red-300 flex items-center justify-center text-[12px] font-bold shrink-0">1</span> Touchez le bouton <Share size={14} className="inline text-sky-400" /> <b>Partager</b> en bas de Safari</li>
                <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-red-600/20 border border-red-700/40 text-red-300 flex items-center justify-center text-[12px] font-bold shrink-0">2</span> Faites défiler et touchez <b>« Sur l'écran d'accueil »</b></li>
                <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-red-600/20 border border-red-700/40 text-red-300 flex items-center justify-center text-[12px] font-bold shrink-0">3</span> Touchez <b>Ajouter</b> — l'icône Code Voiture apparaît 🎉</li>
              </ol>
            ) : (
              <p className="text-white/70 text-[13.5px] leading-relaxed">Ouvrez le menu <b>⋮</b> de votre navigateur puis choisissez <b>« Installer l'application »</b> ou <b>« Ajouter à l'écran d'accueil »</b>.</p>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
